import { api } from '@/lib/api';
import type { ApiActivity, ApiActivityDetail, ApiActivityComment, ActivityFeedParams, CreateActivityDto, ReprogramActivityDto, CreateRecurringActivitiesDto } from '@/lib/types';

export type ActivitiesFeedResult = {
  items: ApiActivity[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

export async function getActivitiesFeed(
  params?: ActivityFeedParams
): Promise<ActivitiesFeedResult> {
  const qs = params
    ? '?' +
    new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => [k, String(v)])
    ).toString()
    : '';

  return api<ActivitiesFeedResult>(`/activities/feed${qs}`);
}

export async function getActivityById(id: string, latitude?: number, longitude?: number): Promise<ApiActivityDetail> {
  const params = new URLSearchParams();

  if (latitude != null && longitude != null) {
    params.set("latitude", String(latitude));
    params.set("longitude", String(longitude));
  }

  const query = params.toString();

  return api<ApiActivityDetail>(
    `/activities/${id}${query ? `?${query}` : ""}`
  );
}

export async function getActivitiesMap(): Promise<ApiActivity[]> {
  return api<ApiActivity[]>('/activities/map');
}

export async function createActivity(dto: CreateActivityDto): Promise<ApiActivity> {
  return api<ApiActivity>('/activities', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export async function deleteActivity(id: string): Promise<void> {
  return api<void>(`/activities/${id}`, { method: 'DELETE' });
}

export async function joinActivity(id: string): Promise<void> {
  return api<void>(`/activities/${id}/join`, { method: 'POST' });
}

export async function leaveActivity(id: string): Promise<void> {
  return api<void>(`/activities/${id}/join`, { method: 'DELETE' });
}

export async function addActivityComment(id: string, content: string): Promise<ApiActivityComment> {
  return api<ApiActivityComment>(`/activities/${id}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

// Supprime un commentaire d'activité.
// Le backend autorise l'auteur du commentaire ainsi que les Admin/Moderator.


export async function deleteActivityComment(activityId: string, commentId: string): Promise<void> {
  return api<void>(`/activities/${activityId}/comments/${commentId}`,
    {
      method: 'DELETE',
    }
  );
}

export async function reportActivity(id: string, reason: string): Promise<void> {
  return api<void>(`/activities/${id}/reports`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function getMyActivities(): Promise<ApiActivity[]> {
  const result = await api<ApiActivity[] | { items?: ApiActivity[]; data?: ApiActivity[] }>('/users/me/activities');
  if (Array.isArray(result)) return result;
  if (result && 'items' in result && result.items) return result.items;
  if (result && 'data' in result && result.data) return result.data;
  return [];
}

export async function getMyParticipations(): Promise<ApiActivity[]> {
  const result = await api<ApiActivity[] | { items?: ApiActivity[]; data?: ApiActivity[] }>('/users/me/participations');
  if (Array.isArray(result)) return result;
  if (result && 'items' in result && result.items) return result.items;
  if (result && 'data' in result && result.data) return result.data;
  return [];
}



export async function reprogramActivity(id: string, dto: ReprogramActivityDto): Promise<{ id: string }> {
  return api<{ id: string }>(`/activities/${id}/reprogram`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}


export async function createNewSessionActivity(id: string, dto: ReprogramActivityDto): Promise<{ id: string }> {
  return api<{ id: string }>(`/activities/${id}/new-session`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}



export async function createRecurringActivities(id: string, dto: CreateRecurringActivitiesDto): Promise<{ activities: { id: string }[] }> {
  return api<{ activities: { id: string }[] }>(
    `/activities/${id}/recurrence`,
    {
      method: 'POST',
      body: JSON.stringify(dto),
    }
  );
}


// ==========================================
// Messagerie privé
// ==========================================

export interface PrivateConversation {
  id: string;
  activityId: string;
  activityTitle: string;
  otherUserId: string;
  otherUserName: string;
  otherUserAvatarUrl: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
}

export interface PrivateMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderAvatarUrl: string | null;
  content: string;
  createdAt: string;
  readAt: string | null;
}

export async function getOrCreatePrivateConversation(
  activityId: string,
  participantId: string
): Promise<PrivateConversation> {
  return api<PrivateConversation>(
    `/private-messages/activities/${activityId}/participants/${participantId}/conversation`,
    {
      method: 'POST',
    }
  );
}

export async function getPrivateConversations(): Promise<PrivateConversation[]> {
  return api<PrivateConversation[]>('/private-messages/conversations');
}

export async function getPrivateConversation(conversationId: string): Promise<PrivateConversation> {
  return api<PrivateConversation>(
    `/private-messages/conversations/${conversationId}`
  );
}

export async function getPrivateMessages(conversationId: string): Promise<PrivateMessage[]> {
  return api<PrivateMessage[]>(
    `/private-messages/conversations/${conversationId}/messages`
  );
}

export async function sendPrivateMessage(conversationId: string, content: string): Promise<PrivateMessage> {
  return api<PrivateMessage>(
    `/private-messages/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      body: JSON.stringify({ content }),
    }
  );
}

export async function markPrivateConversationAsRead(conversationId: string): Promise<void> {
  return api<void>(
    `/private-messages/conversations/${conversationId}/read`,
    {
      method: 'POST',
    }
  );
}